// src/app/providers.tsx
'use client'

import Script from 'next/script'
import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import {
  handleRouteChange,
  isAnalyticsEnabled,
  isTrackablePath,
} from '@/lib/analytics'
import { installAnalyticsNavigationGuard } from '@/lib/analytics-navigation-guard'

export default function GoogleAnalytics() {
  const gaId = process.env.NEXT_PUBLIC_GA4_ID
  // Shared enablement rule (see isAnalyticsEnabled): opt-in flag + ID +
  // production build. Staging and local builds never activate the tag;
  // next.config.mjs fails the build if the flag is set without a valid ID.
  // The leading literal env check repeats one clause of the helper on
  // purpose: it is a compile-time constant, so bundles built with the flag
  // off (staging/local) have the gtag.js loader dead-code-eliminated
  // entirely. The helper remains the single behavioural rule.
  const enabled =
    process.env.NEXT_PUBLIC_ENABLE_ANALYTICS === 'true' && isAnalyticsEnabled()
  const pathname = usePathname()

  useEffect(() => {
    if (!enabled || !gaId) return
    // Synchronous quarantine for unsafe history transitions (private-route
    // pushState via the Header's /patient link, query rewrites, back/forward
    // into unsafe entries). Idempotent; installed here so it is in place
    // before the external gtag.js script can execute — the <Script> below is
    // injected during effects but fetched and run asynchronously afterwards.
    // handleRouteChange remains the only release path for that quarantine.
    installAnalyticsNavigationGuard(gaId)
    if (!pathname) return
    handleRouteChange(gaId, pathname)
  }, [enabled, gaId, pathname])

  // Never inject gtag.js while on a private route (a direct landing on
  // /patient or /studio loads no analytics at all). After a public page has
  // loaded it, the ga-disable flag set in handleRouteChange gates all
  // further collection; next/script dedupes by src, so remounting on a
  // later public route cannot double-inject.
  if (!enabled || !isTrackablePath(pathname)) return null

  return (
    <Script
      src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
      strategy="afterInteractive"
    />
  )
}
